import React, { useState, useCallback, useContext, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { green } from "@material-ui/core/colors";
import {
  Button,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Table,
  TableHead,
  Paper,
  Tooltip,
  Typography,
  CircularProgress,
  Grid
} from "@material-ui/core";
import {
  Edit,
  CheckCircle,
  SignalCellularConnectedNoInternet2Bar,
  SignalCellularConnectedNoInternet0Bar,
  SignalCellular4Bar,
  CropFree,
  DeleteOutline,
  Facebook,
  Instagram,
  WhatsApp
} from "@material-ui/icons";

import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";

import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanies from "../../hooks/useCompanies";
import api from "../../services/api";
import WhatsAppModalCompany from "../../components/CompanyWhatsapps";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import ForbiddenPage from "../../components/ForbiddenPage";

const useStyles = makeStyles(theme => ({
  pageRoot: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    width: "100%",
    maxWidth: "100%",
    padding: theme.spacing(2),
    height: "calc(100% - 48px)",
    overflowY: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },
  summaryCard: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
    padding: theme.spacing(1.5),
    backgroundColor: "#fff",
    height: "100%",
  },
  summaryTitle: {
    color: "#64748b",
    fontSize: "0.75rem",
    marginBottom: theme.spacing(0.5),
  },
  summaryValue: {
    color: "#0f172a",
    fontSize: "1.2rem",
    fontWeight: 700,
  },
  mainPaper: {
    flex: 1,
    overflowY: "auto",
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 12px 26px rgba(17, 24, 39, 0.09)",
    ...theme.scrollbarStyles
  },
  tableWrap: {
    padding: theme.spacing(1.25),
  },
  tableShell: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    overflowX: "auto",
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  tooltip: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: theme.typography.pxToRem(14),
    border: "1px solid #dadde9",
    maxWidth: 450
  },
  tooltipPopper: {
    textAlign: "center"
  },
  buttonProgress: {
    color: green[500]
  },
  tableHeaderCell: {
    fontWeight: 700,
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(15, 23, 42, 0.03)",
    fontSize: "0.76rem",
  },
  tableCellText: {
    fontSize: "0.78rem",
  },
  actionIconButton: {
    border: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(0, 0.2),
  }
}));

const CustomToolTip = ({ title, content, children }) => {
  const classes = useStyles();

  return (
    <Tooltip
      arrow
      classes={{
        tooltip: classes.tooltip,
        popper: classes.tooltipPopper
      }}
      title={
        <React.Fragment>
          <Typography gutterBottom color="inherit">
            {title}
          </Typography>
          {content && <Typography>{content}</Typography>}
        </React.Fragment>
      }
    >
      {children}
    </Tooltip>
  );
};

const IconChannel = channel => {
  switch (channel) {
    case "facebook":
      return <Facebook />;
    case "instagram":
      return <Instagram />;
    case "whatsapp":
      return <WhatsApp />;
    default:
      return "error";
  }
};

const getConnectionType = (connection) => {
  const channel = String(connection?.channel || "").toLowerCase();
  if (channel === "whatsapp_oficial") return "whatsapp_oficial";
  if (channel !== "whatsapp") return channel;

  const provider = String(connection?.provider || "beta").toLowerCase();
  return provider === "wuzapi" ? "whatsapp_wuzapi" : "whatsapp_baileys";
};

const getEffectiveConnectionStatus = (connection) => {
  const channel = String(connection?.channel || "").toLowerCase();
  if (channel !== "whatsapp_oficial") return connection?.status;

  const hasOfficialBinding =
    Boolean(connection?.waba_webhook_id) &&
    Boolean(String(connection?.token || "").trim()) &&
    Boolean(String(connection?.phone_number_id || "").trim());

  return hasOfficialBinding ? "CONNECTED" : "DISCONNECTED";
};

const AllConnections = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { list } = useCompanies();
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(true);
  const [loadingComp, setLoadingComp] = useState(false);
  const [whats, setWhats] = useState([]);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [filterConnections, setFilterConnections] = useState([]);
  const [companyWhatsApps, setCompanyWhatsApps] = useState(null);
  const confirmationModalInitialState = {
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    open: false
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(
    confirmationModalInitialState
  );

  const history = useHistory();
  if (!user.super) {
    history.push("/tickets")
  }

  const getCompanyConnections = useCallback((companyId) => {
    return (whats || []).filter((item) => item?.companyId === companyId);
  }, [whats]);

  const getConnectedCountByType = useCallback((connections, type) => {
    return connections.filter(
      (item) =>
        getEffectiveConnectionStatus(item) === "CONNECTED" &&
        getConnectionType(item) === type
    ).length;
  }, []);

  const getConnectedTotal = useCallback((connections) => {
    return connections.filter((item) => getEffectiveConnectionStatus(item) === "CONNECTED").length;
  }, []);

  const totals = useMemo(() => {
    const list = whats || [];
    const connectedTotal = getConnectedTotal(list);
    const connectedBaileys = getConnectedCountByType(list, "whatsapp_baileys");
    const connectedWuzapi = getConnectedCountByType(list, "whatsapp_wuzapi");
    const connectedOfficial = getConnectedCountByType(list, "whatsapp_oficial");

    return {
      connectedTotal,
      connectedBaileys,
      connectedWuzapi,
      connectedOfficial,
      disconnectedTotal: list.length - connectedTotal,
      total: list.length,
    };
  }, [whats, getConnectedTotal, getConnectedCountByType]);


  useEffect(() => {
    setLoadingWhatsapp(true);
    const fetchSession = async () => {
      try {
        const { data } = await api.get("/whatsapp/all/?session=0");
        setWhats(data);
        setLoadingWhatsapp(false);
      } catch (err) {
        setLoadingWhatsapp(false);
        toastError(err);
      }
    };
    fetchSession();
  }, []);

  const responseFacebook = response => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          facebookUserId: id,
          facebookUserToken: accessToken
        })
        .then(response => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch(error => {
          toastError(error);
        });
    }
  };
  useEffect(() => {
    loadCompanies();
  }, []);
  const loadCompanies = async () => {
    setLoadingComp(true);
    try {
      const companyList = await list();
      setCompanies(companyList);
    } catch (e) {
      toast.error("Não foi possível carregar a lista de registros");
    }
    setLoadingComp(false);
  }

  const responseInstagram = response => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          addInstagram: true,
          facebookUserId: id,
          facebookUserToken: accessToken
        })
        .then(response => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch(error => {
          toastError(error);
        });
    }
  };

  const handleStartWhatsAppSession = async whatsAppId => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async whatsAppId => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenWhatsAppModal = (whatsappsFilter, comp) => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
    if (whatsappsFilter?.length > 0) {
      setFilterConnections(whatsappsFilter);
      setCompanyWhatsApps(comp);
    }
  };



  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
    setFilterConnections([]);
    setCompanyWhatsApps(null);
  }, [setSelectedWhatsApp, setWhatsAppModalOpen]);

  const handleOpenQrModal = whatsApp => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, [setQrModalOpen, setSelectedWhatsApp]);

  const handleEditWhatsApp = whatsApp => {
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId: whatsAppId
      });
    }

    if (action === "delete") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId: whatsAppId
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      } catch (err) {
        toastError(err);
      }
    }

    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      }
    }

    setConfirmModalInfo(confirmationModalInitialState);
  };

  const renderActionButtons = whatsApp => {
    return (
      <>
        {whatsApp.status === "qrcode" && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => handleOpenQrModal(whatsApp)}
          >
            {i18n.t("connections.buttons.qrcode")}
          </Button>
        )}
        {whatsApp.status === "DISCONNECTED" && (
          <>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => handleStartWhatsAppSession(whatsApp.id)}
            >
              {i18n.t("connections.buttons.tryAgain")}
            </Button>{" "}
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => handleRequestNewQrCode(whatsApp.id)}
            >
              {i18n.t("connections.buttons.newQr")}
            </Button>
          </>
        )}
        {(whatsApp.status === "CONNECTED" ||
          whatsApp.status === "PAIRING" ||
          whatsApp.status === "TIMEOUT") && (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => {
                handleOpenConfirmationModal("disconnect", whatsApp.id);
              }}
            >
              {i18n.t("connections.buttons.disconnect")}
            </Button>
          )}
        {whatsApp.status === "OPENING" && (
          <Button size="small" variant="outlined" disabled color="default">
            {i18n.t("connections.buttons.connecting")}
          </Button>
        )}
      </>
    );
  };

  const renderStatusToolTips = whatsApp => {
    return (
      <div className={classes.customTableCell}>
        {whatsApp.status === "DISCONNECTED" && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.disconnected.title")}
            content={i18n.t("connections.toolTips.disconnected.content")}
          >
            <SignalCellularConnectedNoInternet0Bar color="secondary" />
          </CustomToolTip>
        )}
        {whatsApp.status === "OPENING" && (
          <CircularProgress size={24} className={classes.buttonProgress} />
        )}
        {whatsApp.status === "qrcode" && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.qrcode.title")}
            content={i18n.t("connections.toolTips.qrcode.content")}
          >
            <CropFree />
          </CustomToolTip>
        )}
        {whatsApp.status === "CONNECTED" && (
          <CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
            <SignalCellular4Bar style={{ color: green[500] }} />
          </CustomToolTip>
        )}
        {(whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.timeout.title")}
            content={i18n.t("connections.toolTips.timeout.content")}
          >
            <SignalCellularConnectedNoInternet2Bar color="secondary" />
          </CustomToolTip>
        )}
      </div>
    );
  };
  return (
    <div className={classes.pageRoot}>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
      <QrcodeModal
        open={qrModalOpen}
        onClose={handleCloseQrModal}
        whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
      />
      <WhatsAppModalCompany
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        filteredWhatsapps={filterConnections}
        companyInfos={companyWhatsApps}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
      />

      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <Grid container spacing={2} style={{ marginBottom: 12 }}>
            <Grid item xs={6} md={2}>
              <Paper className={classes.summaryCard} elevation={0}>
                <Typography className={classes.summaryTitle}>Conectadas (Total)</Typography>
                <Typography className={classes.summaryValue}>{totals.connectedTotal}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={2}>
              <Paper className={classes.summaryCard} elevation={0}>
                <Typography className={classes.summaryTitle}>Baileys</Typography>
                <Typography className={classes.summaryValue}>{totals.connectedBaileys}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={2}>
              <Paper className={classes.summaryCard} elevation={0}>
                <Typography className={classes.summaryTitle}>WuzAPI</Typography>
                <Typography className={classes.summaryValue}>{totals.connectedWuzapi}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={2}>
              <Paper className={classes.summaryCard} elevation={0}>
                <Typography className={classes.summaryTitle}>API Oficial</Typography>
                <Typography className={classes.summaryValue}>{totals.connectedOfficial}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={2}>
              <Paper className={classes.summaryCard} elevation={0}>
                <Typography className={classes.summaryTitle}>Desconectadas</Typography>
                <Typography className={classes.summaryValue}>{totals.disconnectedTotal}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={2}>
              <Paper className={classes.summaryCard} elevation={0}>
                <Typography className={classes.summaryTitle}>Empresas</Typography>
                <Typography className={classes.summaryValue}>{companies?.length || 0}</Typography>
              </Paper>
            </Grid>
          </Grid>

          <Paper className={classes.mainPaper} variant="outlined">
            <MainHeader>
              <Title>{i18n.t("connections.title")}</Title>
              <MainHeaderButtonsWrapper>
                <PopupState variant="popover" popupId="demo-popup-menu">
                  {popupState => (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        {...bindTrigger(popupState)}
                      >
                        {i18n.t("connections.newConnection")}
                      </Button>
                      <Menu {...bindMenu(popupState)}>
                        <MenuItem
                          onClick={() => {
                            handleOpenWhatsAppModal();
                            popupState.close();
                          }}
                        >
                          <WhatsApp
                            fontSize="small"
                            style={{
                              marginRight: "10px"
                            }}
                          />
                          WhatsApp (Baileys)
                        </MenuItem>
                        <MenuItem disabled>
                          <WhatsApp
                            fontSize="small"
                            style={{
                              marginRight: "10px"
                            }}
                          />
                          WhatsApp (wuzAPI)
                        </MenuItem>
                        <FacebookLogin
                          appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                          autoLoad={false}
                          fields="name,email,picture"
                          version="13.0"
                          scope="public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management"
                          callback={responseFacebook}
                          render={renderProps => (
                            <MenuItem onClick={renderProps.onClick}>
                              <Facebook
                                fontSize="small"
                                style={{
                                  marginRight: "10px"
                                }}
                              />
                              Facebook
                            </MenuItem>
                          )}
                        />

                        <FacebookLogin
                          appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                          autoLoad={false}
                          fields="name,email,picture"
                          version="13.0"
                          scope="public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management"
                          callback={responseInstagram}
                          render={renderProps => (
                            <MenuItem onClick={renderProps.onClick}>
                              <Instagram
                                fontSize="small"
                                style={{
                                  marginRight: "10px"
                                }}
                              />
                              Instagram
                            </MenuItem>
                          )}
                        />
                      </Menu>
                    </>
                  )}
                </PopupState>
              </MainHeaderButtonsWrapper>
            </MainHeader>
            <div className={classes.tableWrap}>
              <div className={classes.tableShell}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeaderCell} align="center">
                        Cliente
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell} align="center">
                        Conectadas (Baileys)
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell} align="center">
                        Conectadas (WuzAPI)
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell} align="center">
                        Conectadas (API Oficial)
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell} align="center">
                        Conectadas (Total)
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell} align="center">
                        Desconectadas
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell} align="center">
                        Total de Conexões
                      </TableCell>
                      {user.profile === "admin" && (
                        <TableCell className={classes.tableHeaderCell} align="center">
                          {i18n.t("connections.table.actions")}
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingWhatsapp ? (
                      <TableRowSkeleton />
                    ) : (
                      <>
                        {companies?.length > 0 && companies.map(company => (
                          <TableRow key={company.id} hover>
                            <TableCell className={classes.tableCellText} align="center">
                              {company?.name}
                            </TableCell>
                            <TableCell className={classes.tableCellText} align="center">
                              {getConnectedCountByType(getCompanyConnections(company?.id), "whatsapp_baileys")}
                            </TableCell>
                            <TableCell className={classes.tableCellText} align="center">
                              {getConnectedCountByType(getCompanyConnections(company?.id), "whatsapp_wuzapi")}
                            </TableCell>
                            <TableCell className={classes.tableCellText} align="center">
                              {getConnectedCountByType(getCompanyConnections(company?.id), "whatsapp_oficial")}
                            </TableCell>
                            <TableCell className={classes.tableCellText} align="center">
                              {getConnectedTotal(getCompanyConnections(company?.id))}
                            </TableCell>
                            <TableCell className={classes.tableCellText} align="center">
                              {getCompanyConnections(company?.id).length - getConnectedTotal(getCompanyConnections(company?.id))}
                            </TableCell>
                            <TableCell className={classes.tableCellText} align="center">
                              {getCompanyConnections(company?.id).length}
                            </TableCell>
                            {user.profile === "admin" && (
                              <TableCell align="center">
                                <IconButton
                                  className={classes.actionIconButton}
                                  size="small"
                                  onClick={() => handleOpenWhatsAppModal(getCompanyConnections(company?.id), company)}
                                >
                                  <Edit />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        )
                        )}
                        <TableRow>
                          <TableCell className={classes.tableHeaderCell} align="center">{i18n.t("Total")}</TableCell>
                          <TableCell className={classes.tableHeaderCell} align="center">
                            {totals.connectedBaileys}
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell} align="center">
                            {totals.connectedWuzapi}
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell} align="center">
                            {totals.connectedOfficial}
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell} align="center">
                            {totals.connectedTotal}
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell} align="center">
                            {totals.disconnectedTotal}
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell} align="center">
                            {totals.total}
                          </TableCell>
                          {user.profile === "admin" && <TableCell className={classes.tableHeaderCell} align="center"></TableCell>}
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </Paper>
        </>
      )}
    </div>
  );
};

export default AllConnections;
